"use client";

import { Button } from "@universe/ui/components/button";
import { Calendar } from "@universe/ui/components/calendar";
import { Label } from "@universe/ui/components/label";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@universe/ui/components/popover";
import { cn } from "@universe/ui/lib/utils";
import { format } from "date-fns";
import { AnimatePresence, motion } from "framer-motion";
import { Calendar as CalendarIcon, X } from "lucide-react";
import { useTranslations } from "next-intl";
import { useQueryState } from "nuqs";
import * as React from "react";
import { useEffect } from "react";
import { type DateRange, type DayPickerProps } from "react-day-picker";

import { DATE_PARAM_FORMAT } from "@/constants/params";

type DatePickerProps = {
  selected?: DateRange;
  label?: string;
  placeholder?: string;
  onDateRangeChange: (range: DateRange | undefined) => void;
  onClearDateRange: () => void;
  mode?: "range";
  disabledDates?: (date: Date) => boolean;
  className?: string;
  /** Extra classes for the trigger button only; `className` styles the wrapper. */
  triggerClassName?: string;
  /** Extra classes for the clear button shown once a range is selected. */
  clearButtonClassName?: string;
} & Omit<DayPickerProps, "mode">;

export function DatePickerRange({
  selected,
  label,
  placeholder,
  onDateRangeChange,
  onClearDateRange,
  numberOfMonths = 1,
  disabledDates,
  className,
  triggerClassName,
  clearButtonClassName,
}: DatePickerProps) {
  const tDate = useTranslations("date");
  const tCommon = useTranslations("common");
  const [isOpen, setIsOpen] = React.useState(false);
  const [hasInteracted, setHasInteracted] = React.useState(false);

  const [fromDate, setFromDate] = useQueryState("fromDate");
  const [toDate, setToDate] = useQueryState("toDate");

  const [dateRange, setDateRange] = React.useState<DateRange | undefined>(
    () => {
      // Initialize with URL params if available, otherwise use selected prop
      if (fromDate && toDate) {
        return {
          from: new Date(fromDate),
          to: new Date(toDate),
        };
      }
      return (
        selected || {
          from: undefined,
          to: undefined,
        }
      );
    }
  );

  const handleSetDateRange = (range: DateRange | undefined) => {
    setDateRange(range);
    if (range?.from && range?.to) {
      setFromDate(format(range.from, DATE_PARAM_FORMAT));
      setToDate(format(range.to, DATE_PARAM_FORMAT));
    } else {
      setFromDate("");
      setToDate("");
    }
    setHasInteracted(true);
  };

  const handleClearDateRange = () => {
    handleSetDateRange({
      from: undefined,
      to: undefined,
    });
    setFromDate("");
    setToDate("");
    onClearDateRange();
  };

  const handleSetDateRangeAndClose = (range: DateRange) => {
    handleSetDateRange(range);
    setHasInteracted(true);
    onDateRangeChange(range);
    setIsOpen(false);
  };

  const handleApplyDateRange = () => {
    setDateRange(dateRange);
    onDateRangeChange(dateRange);
    setIsOpen(false);
  };

  // Run only on mount
  useEffect(() => {
    // On mount, if we have URL params and haven't interacted yet, notify parent
    if (!hasInteracted && fromDate && toDate) {
      const urlDateRange = {
        from: new Date(fromDate),
        to: new Date(toDate),
      };
      // eslint-disable-next-line react-hooks/set-state-in-effect -- Initialize from URL params
      setDateRange(urlDateRange);
      // Defense-in-depth: only push the URL range up to the parent if the
      // parent isn't ALREADY showing it. `hasInteracted` is local state that
      // resets to false on every remount while the ?fromDate/?toDate URL
      // params persist, so an unconditional notify here re-applies the filter
      // on each remount — and any parent that conditionally renders this picker
      // based on filter state (e.g. LedgerEntryHeader's skeleton swap) then
      // loops. Skip the notify when `selected` already equals the URL range.
      const alreadyApplied =
        !!selected?.from &&
        !!selected?.to &&
        format(selected.from, DATE_PARAM_FORMAT) === fromDate &&
        format(selected.to, DATE_PARAM_FORMAT) === toDate;
      if (!alreadyApplied) {
        onDateRangeChange(urlDateRange);
      }
    } else if (!hasInteracted && selected) {
      // If no URL params but selected prop is provided
      setDateRange(selected);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Separate effect for handling selected prop changes after interaction
  useEffect(() => {
    if (!hasInteracted && selected && !fromDate && !toDate) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- Sync with selected prop
      setDateRange(selected);
    }
  }, [selected, hasInteracted, fromDate, toDate]);

  return (
    <div
      className={cn(
        "flex flex-col w-full",
        label && "gap-3 lg:gap-5",
        className
      )}
    >
      {label && <Label className="font-medium lg:text-base">{label}</Label>}
      <Popover open={isOpen} onOpenChange={setIsOpen}>
        <div className="flex items-center justify-center gap-4 w-full relative">
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              data-empty={!dateRange}
              className={cn(
                "data-[empty=true]:text-muted-foreground flex-1 lg:w-[315px] xl:w-[440px] text-sm font-normal gap-2 overflow-hidden hover:cursor-pointer",
                triggerClassName
              )}
              size="default"
            >
              <motion.div
                layout={hasInteracted}
                transition={{ duration: 0.2, ease: "easeInOut" }}
                className="flex items-center gap-2 w-full justify-between text-center"
              >
                <CalendarIcon />
                <AnimatePresence mode="wait">
                  {dateRange?.from && dateRange?.to ? (
                    <motion.span
                      key="date-range"
                      initial={hasInteracted ? { opacity: 0, y: 10 } : false}
                      animate={{ opacity: 1, y: 0 }}
                      exit={hasInteracted ? { opacity: 0, y: -10 } : undefined}
                      transition={{ duration: 0.2 }}
                      className="truncate mr-4"
                    >
                      {`${format(dateRange.from as Date, "PP")} - ${format(
                        dateRange.to as Date,
                        "PP"
                      )}`}
                    </motion.span>
                  ) : (
                    <motion.span
                      key="placeholder"
                      initial={hasInteracted ? { opacity: 0, y: 10 } : false}
                      animate={{ opacity: 1, y: 0 }}
                      exit={hasInteracted ? { opacity: 0, y: -10 } : undefined}
                      transition={{ duration: 0.2 }}
                      className="truncate mr-4"
                    >
                      {placeholder || tDate("selectDates")}
                    </motion.span>
                  )}
                </AnimatePresence>
                <div />
              </motion.div>
            </Button>
          </PopoverTrigger>
          {dateRange?.from && dateRange?.to && (
            <Button
              variant="ghost"
              size="icon"
              type="button"
              aria-label={tCommon("clear")}
              onClick={handleClearDateRange}
              className={cn("absolute right-0", clearButtonClassName)}
            >
              <X className="size-4" />
            </Button>
          )}
        </div>
        <PopoverContent className="w-auto py-4 px-6">
          <Calendar
            showOutsideDays={false}
            mode="range"
            defaultMonth={dateRange?.from}
            selected={dateRange}
            numberOfMonths={numberOfMonths}
            onSelect={handleSetDateRange}
            disabled={disabledDates}
          />

          <div className="flex flex-col lg:flex-row gap-3 p-3 pb-0 mt-6 justify-end ">
            <Button
              variant="outline"
              onClick={() =>
                handleSetDateRangeAndClose({
                  from: new Date(
                    new Date().getFullYear(),
                    new Date().getMonth(),
                    1
                  ),
                  to: new Date(),
                })
              }
            >
              {tDate("thisMonth")}
            </Button>
            <Button
              variant="outline"
              onClick={() =>
                handleSetDateRangeAndClose({
                  from: new Date(
                    new Date().getFullYear(),
                    new Date().getMonth() - 1,
                    1
                  ),
                  to: new Date(
                    new Date().getFullYear(),
                    new Date().getMonth(),
                    0
                  ),
                })
              }
            >
              {tDate("lastFullMonth")}
            </Button>
            <Button
              onClick={(e) => {
                e.preventDefault();
                handleApplyDateRange();
              }}
            >
              {tDate("apply")}
            </Button>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}
